// Reels: full-screen 9:16 feed of short previews.
// Auto-scroll (on by default) moves to the next reel when one ends.
// People can also swipe, scroll the mouse wheel, use ↑/↓ keys or the arrow buttons.
import { useCallback, useEffect, useRef, useState, type WheelEvent } from 'react';
import { api } from '../lib/api';
import { useZoomLock } from '../lib/antipiracy';
import { useCatalog } from '../lib/catalog';
import { useQuery } from '../lib/query';
import { Link, navigate } from '../lib/router';
import { getState, hasPlus, setState, toast, useApp } from '../lib/store';
import { useDesktop } from '../lib/useMedia';
import { useVerticalSwipe } from '../lib/useSwipe';
import { haptic } from '../lib/haptics';
import { prefetchEpisode } from '../lib/prefetch';
import { EpisodeSheet, useSheet } from '../components/EpisodeSheet';
import { Icon } from '../components/Icon';
import { Player } from '../components/Player';
import { AppShell, ReelBuffering } from '../components/Shell';

export function Reels() {
  const { shows: SHOWS, episodes: EPISODES } = useCatalog();
  useZoomLock(true);
  const desktop = useDesktop();
  const reels = useQuery('reels', () => api.reels());
  const auto = useApp((s) => s.settings.autoScroll);
  const likes = useApp((s) => s.likes);
  const [i, setI] = useState(0);
  const [dir, setDir] = useState<'up' | 'down' | 'none'>('down');
  const lastWheel = useRef(0);
  // Only reels whose show and episode are in the catalogue (a reel can briefly outlive an unpublished show).
  const list = (reels.data || []).filter((r) => SHOWS.some((s) => s.id === r.showId) && EPISODES.some((e) => e.id === r.episodeId));
  const plus = useApp((s) => hasPlus(s));
  const episodesBtn = useRef<HTMLButtonElement>(null);
  const sheet = useSheet(episodesBtn);
  const n = list.length;

  const go = useCallback((d: 1 | -1, fromKey = false) => {
    if (!n) return;
    setDir(fromKey ? 'none' : d > 0 ? 'down' : 'up');
    setI((x) => (x + d + n) % n);
  }, [n]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown' || e.key === 'PageDown') { e.preventDefault(); go(1, true); }
      if (e.key === 'ArrowUp' || e.key === 'PageUp') { e.preventDefault(); go(-1, true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  const onWheel = (e: WheelEvent) => {
    const now = Date.now();
    if (now - lastWheel.current < 650 || Math.abs(e.deltaY) < 20) return;
    lastWheel.current = now;
    go(e.deltaY > 0 ? 1 : -1);
  };
  // Start loading the full episode behind the current reel, so "Watch the full episode" opens instantly.
  const cur = list[i];
  useEffect(() => {
    if (!cur || cur.locked) return;
    const ep = EPISODES.find((e) => e.id === cur.episodeId);
    if (!ep) return;
    const t = window.setTimeout(() => prefetchEpisode(cur.showId, ep.no, plus), 500);
    return () => window.clearTimeout(t);
  }, [cur, plus]);

  const swipe = useVerticalSwipe({
    onNext: () => go(1),
    onPrev: () => go(-1),
    ignore: (t) => !!(t as HTMLElement | null)?.closest?.('.reel-side, .reel-info a, .reels-top, .ep-sheet'),
    disabled: !!sheet.state,
  });

  const onEnded = useCallback(() => {
    if (getState().settings.autoScroll) go(1);
  }, [go]);

  if (!n) return <ReelBuffering desktop={desktop} bottomNav />;

  const r = list[i];
  const show = SHOWS.find((s) => s.id === r.showId)!;
  const ep = EPISODES.find((e) => e.id === r.episodeId)!;
  const liked = !!likes[r.episodeId];

  const toggleAuto = () => setState((s) => ({ settings: { ...s.settings, autoScroll: !s.settings.autoScroll } }));
  const like = () => { haptic('tap'); return setState((s) => {
    const l = { ...s.likes };
    if (l[r.episodeId]) delete l[r.episodeId]; else l[r.episodeId] = true;
    return { likes: l };
  }); };

  const feed = (
    <div className={'reels' + (desktop ? ' reels-desktop' : '')} onWheel={onWheel} {...swipe.handlers}>
      <div className="reels-top">
        <h1>Reels</h1>
        <button type="button" role="switch" aria-checked={auto} className={'switch-pill' + (auto ? ' on' : '')} onClick={toggleAuto}>
          <span className="switch-dot" />Auto-scroll
        </button>
      </div>

      <div key={r.id} ref={swipe.slideRef} className={'reel-slide slide-' + dir}>
        <Player
          episodeKey={r.id}
          caption={r.caption}
          color={r.color}
          durationS={r.durationS}
          mode="reel"
          onEnded={onEnded}
        />
      </div>

      <div className="reel-side">

      <div className="reel-actions">
        <button type="button" onClick={like} aria-pressed={liked} className={liked ? 'on' : ''}><Icon name="heart" size={28} /><span>Like</span></button>
        <button type="button" onClick={() => { haptic('tap'); setState((s) => ({ saves: { ...s.saves, [show.id]: true } })); toast('Saved to your library'); }}><Icon name="bookmark" size={28} /><span>Save</span></button>
        <button type="button" onClick={() => navigator.clipboard?.writeText(`https://kuchnaya.in/show/${show.slug}`).then(() => toast('Link copied'), () => toast('kuchnaya.in/show/' + show.slug))}><Icon name="share" size={28} /><span>Share</span></button>
        <button type="button" ref={episodesBtn} onClick={sheet.open} aria-haspopup="dialog"><Icon name="list" size={28} /><span>Episodes</span></button>
      </div>
      </div>

      <div className="reel-info">
        <span className="creator"><span className="avatar sm accent">{show.creator.name[0]}</span><span><b>{show.title}</b><small>Ep {ep.no} · {ep.title} · {show.creator.name}</small></span></span>
        <small className="muted">{auto ? 'Auto-scroll is on: the next reel starts by itself. Swipe or scroll to move.' : 'Swipe or scroll up and down, or use the arrows.'}</small>
        {r.locked ? (
          <Link to={`/plus?show=${show.id}&ep=${ep.no}`} className="unlock-card">
            <span className="unlock-icon"><Icon name="lock" size={16} /></span>
            <span>This is a preview.<b>Unlock the full episode with Plus</b></span>
            <Icon name="right" size={18} />
          </Link>
        ) : (
          <Link to={`/watch/${show.id}/${ep.no}`} className="unlock-card free">
            <span className="unlock-icon"><Icon name="play" size={14} /></span>
            <span>Watch the full episode<b>{ep.title}</b></span>
            <Icon name="right" size={18} />
          </Link>
        )}
      </div>
      {sheet.state && (() => {
        const eps = EPISODES.filter((e) => e.showId === show.id);
        const locked = (n: number) => { const x = eps.find((e) => e.no === n); return !!x && show.access === 'plus' && !x.isFreePreview && !plus; };
        return (
          <EpisodeSheet
            title={show.title}
            color={show.color}
            episodes={eps}
            currentNo={ep.no}
            isLocked={locked}
            closing={sheet.state === 'closing'}
            onClose={() => sheet.close()}
            onPick={(n) => { sheet.dismissNow(); navigate(locked(n) ? `/plus?show=${show.id}&ep=${n}` : `/watch/${show.id}/${n}`); }}
          />
        );
      })()}
    </div>
  );

  return <AppShell mobileHeader={false} flush>{feed}</AppShell>;
}
